import { useEffect } from 'react';

import { LinearProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import {
  SearchUtils,
  useRecentlyVisited,
} from '@spotify/confidence-core-react';
import {
  GetWorkflowInstanceQuery,
  Maybe,
  WorkflowInstanceCheckFragment,
  WorkflowInstanceFragmentDoc,
  getError,
  getTypeOrNull,
  isType,
  useGetWorkflowInstanceQuery,
  useOnWorkflowInstanceUpdatedSubscription,
} from '@spotify/confidence-graphql';

import { getWorkflowInstanceName } from '../../helpers/workflowInstanceUtils';

const getIntermediateRenderComponent = ({
  loading,
  error,
  workflowInstance,
}: {
  loading: boolean;
  error?: Maybe<string>;
  workflowInstance: WorkflowInstance | null;
}) => {
  if (workflowInstance) {
    return null;
  }

  if (loading) {
    const LoadingComponent = () => <LinearProgress />;
    LoadingComponent.displayName = 'LoadingComponent';
    return LoadingComponent;
  }

  if (error) {
    const ErrorComponent = () => <Alert severity="error">{error}</Alert>;
    ErrorComponent.displayName = 'ErrorComponent';
    return ErrorComponent;
  }

  return null;
};
export type WorkflowInstance = Extract<
  GetWorkflowInstanceQuery['workflowInstance'],
  { __typename: 'WorkflowV1WorkflowInstance' }
>;

export type WorkflowInstanceCheck = WorkflowInstanceCheckFragment;

type UseGetWorkflowInstance = ReturnType<typeof useGetWorkflowInstanceQuery> & {
  loading: boolean;
  workflowInstance: WorkflowInstance | null;
  IntermediateRenderComponent: (() => JSX.Element) | null;
  errorMessage: string | undefined;
};

export const useGetWorkflowInstance = ({
  instanceId,
  workflowId,
  skip,
  trackVisit = false,
}: {
  instanceId: string;
  workflowId: string;
  skip?: boolean;
  // Set to true only when used on the actual workflow instance page
  trackVisit?: boolean;
}): UseGetWorkflowInstance => {
  const { triggerRecentlyVisited } = useRecentlyVisited();
  const workflowInstanceName = getWorkflowInstanceName(workflowId, instanceId);

  const queryResult = useGetWorkflowInstanceQuery({
    variables: {
      name: workflowInstanceName,
    },
    onCompleted: response => {
      if (
        trackVisit &&
        isType(response.workflowInstance, 'WorkflowV1WorkflowInstance')
      ) {
        triggerRecentlyVisited(response.workflowInstance.displayName);
      }
    },
    skip,
  });

  useOnWorkflowInstanceUpdatedSubscription({
    variables: {
      filter: `workflowInstance.name.keyword:"${SearchUtils.escapeQuery(workflowInstanceName)}"`,
    },
    skip: skip || !workflowId || !instanceId,
    onData: payload => {
      const instance =
        payload.data.data?.workflowInstanceUpdated?.workflowInstance;
      if (instance) {
        const cacheId = payload.client.cache.identify(instance);
        if (cacheId) {
          payload.client.cache.writeFragment({
            id: cacheId,
            data: instance,
            fragment: WorkflowInstanceFragmentDoc,
            fragmentName: 'WorkflowInstance',
          });
        }
      }
    },
  });

  const { data, loading, startPolling, stopPolling } = queryResult;

  useEffect(() => {
    if (isType(data?.workflowInstance, 'WorkflowV1WorkflowInstance')) {
      const hasPendingTransition = !!data?.workflowInstance?.pendingTransition;
      const hasRunningTransition =
        !!data?.workflowInstance?.transitionStatus?.running;

      if (hasPendingTransition || hasRunningTransition) {
        // Poll more frequently during transitions to ensure UI updates promptly
        startPolling(5000);
      } else {
        stopPolling();
      }
    } else {
      stopPolling();
    }
  }, [startPolling, stopPolling, data?.workflowInstance]);

  const workflowInstance = getTypeOrNull(data?.workflowInstance);

  const errorMessage =
    getError(data?.workflowInstance)?.message ?? queryResult.error?.message;

  const IntermediateRenderComponent = getIntermediateRenderComponent({
    loading: queryResult.loading,
    error: errorMessage,
    workflowInstance,
  });

  return {
    ...queryResult,
    loading,
    workflowInstance,
    IntermediateRenderComponent,
    errorMessage,
  };
};
